// use-drip-actions.ts — the single source of truth for a campaign's overflow
// actions (Pause/Resume, Edit/Rename, Duplicate, Delete). Extracted VERBATIM in
// behavior from `sequence-detail.tsx`'s inline `actionItems` construction so BOTH
// the detail header AND the list cards (`CampaignCard`) drive the exact same
// action machinery — no reimplementation, no drift.
//
// The hook is nav-agnostic: navigation to the wizard is injected via `onEdit`
// (used for both Edit and opening a freshly Duplicated copy), and the optional
// `onDeleted` fires after a delete (the detail uses it to return to the list; the
// card omits it since the deleted row simply drops out of the list query).

import React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  DBDripThunks,
  DBCampaignThunks,
  campaignsSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import { usePauseDripMutation, useResumeDripMutation, useAbortDripMutation, useSkipNextOccurrenceMutation } from '@common/reducers/backend-api/backend-api';

import { PauseCircle, PlayCircle, Pencil, Copy, Trash2, CalendarClock, Repeat, Bookmark, SkipForward } from 'lucide-react';

import { isRecurringCampaign, stopRecurrenceSchedulePatch } from '@common-reducers/campaign-series';

import type { Drip } from '@bt-common/schemas/bt/drip';
import type { Campaign } from '@common-models/campaign-model';

import type { ActionItem } from './action-menu';
import { menuEditTargetFor, standaloneEditTargetFor } from './campaign-editability';
import { standaloneCampaignActionKeys } from './standalone-campaign-actions';
import { duplicateCampaignThunk } from '@common-reducers/CampaignReducer';

export interface UseDripActionsInput {
  /** The synthesized/effective drip (may be a standalone-campaign bridge). */
  drip?: Drip;
  /** The real Drip doc (undefined for a standalone campaign). */
  realDrip?: Drip;
  /** The standalone campaign, when this row is a legacy drip-less campaign. */
  standaloneCampaign?: Campaign;
  dripId: string;
  isStandalone: boolean;
  /** Navigate to the wizard — used for Edit and to open a Duplicated copy. */
  onEdit?: (dripId: string) => void;
  /** #103: open the scoped in-flight content editor (message/attachment only)
   *  for the given campaign. Wired by both surfaces that render this hook —
   *  `sequence-detail.tsx` (the detail header) and `management-list.tsx` (the
   *  list cards) — each of which owns the modal's mount/unmount and nothing
   *  else. Left optional so a future surface can adopt the hook without it. */
  onEditContent?: (campaignId: string) => void;
  /** #103: open the TIER-1 SCHEDULED-campaign editor
   *  (`ScheduledCampaignEditModal`) for a STANDALONE campaign that has been
   *  launched but has not started sending — message, audience and send time.
   *  Gated on the callback exactly like `onEditStep`: a surface that cannot
   *  mount the modal offers no tier-1 edit rather than falling back to the
   *  wizard, which on a campaign id hydrates nothing and hangs on "Loading…". */
  onEditSchedule?: (campaignId: string) => void;
  /** #103: open the TIER-1 step editor (`DripStepEditModal`) on a launched but
   *  not-yet-dispatching step. Required for the tier-1 item to appear at all:
   *  the alternative — the launch wizard — RE-LAUNCHES a launched drip and
   *  double-sends its audience (see `menuEditTargetFor`), so a surface that
   *  cannot mount the step editor offers no tier-1 edit rather than a
   *  dangerous one. */
  onEditStep?: (stepIndex: number) => void;
  /** Fired after a delete completes (detail: return to the list). */
  /** Fired after a delete. `result.abortFailed` is true when the backend stop
   *  (abortDrip) rejected — the caller can surface a "couldn't stop sending"
   *  warning (#590) instead of silently claiming success. */
  onDeleted?: (result?: { abortFailed?: boolean }) => void;
}

export interface UseDripActionsResult {
  items: ActionItem[];
  renaming: boolean;
  nameInput: string;
  setNameInput: (v: string) => void;
  startRename: () => void;
  commitRename: () => void;
  /** Close the rename input WITHOUT saving (the header/card Escape handler). */
  cancelRename: () => void;
  /** FE#331 — whether the "Save as template" modal is open. The surface renders
   *  `<SaveAsDripTemplateModal open={savingTemplate} … onClose={stopSaveTemplate} />`. */
  savingTemplate: boolean;
  startSaveTemplate: () => void;
  stopSaveTemplate: () => void;
  /** A LIVE campaign (active/paused) asks before deletion, because deleting
   *  it stops sending for everyone who has not received it yet. The surface
   *  renders the dialog; a draft keeps the lighter click-again gesture. */
  confirmingDelete: boolean;
  confirmDelete: () => void;
  cancelDelete: () => void;
}

export function useDripActions(input: UseDripActionsInput): UseDripActionsResult {
  const { drip, realDrip, standaloneCampaign, dripId, isStandalone, onEdit, onEditContent, onEditSchedule, onEditStep, onDeleted } = input;

  const dispatch = useAppDispatch();
  const [pauseDrip] = usePauseDripMutation();
  const [resumeDrip] = useResumeDripMutation();
  const [abortDrip] = useAbortDripMutation();
  const [skipNextOccurrence] = useSkipNextOccurrenceMutation();

  // The drip's step-campaigns (real campaigns tagged with the dripId). Delete
  // aborts these so any queued/in-flight dispatch stops. Selected internally so
  // both the detail and the card get the same machinery with no extra plumbing.
  const stepCampaigns = useAppSelector((s) =>
    campaignsSelectByQuery(s.DBCampaignReducer, { dripId }),
  ) as Campaign[];

  // #103: index step-campaigns by their dripStepIndex, the same way
  // sequence-detail.tsx's `campaignByStep` does, so the edit-action lookup below
  // resolves the CURRENT step's campaign without a second fetch/selector.
  const campaignByStep = React.useMemo(() => {
    const m = new Map<number, Campaign>();
    for (const c of stepCampaigns) {
      if (typeof c.dripStepIndex === 'number') m.set(c.dripStepIndex, c);
    }
    return m;
  }, [stepCampaigns]);
  const currentStepIndex = realDrip?.currentStep ?? 0;
  const currentStepCampaign = campaignByStep.get(currentStepIndex);

  // #103: the campaign ids the content editor is opened on. Normalised to a
  // string here (the model types `_id` as `string | number | null`) so the two
  // call sites below can gate on presence rather than fall back to a wrong id.
  const stepCampaignId = currentStepCampaign?._id != null ? String(currentStepCampaign._id) : undefined;
  const standaloneCampaignId = standaloneCampaign?._id != null ? String(standaloneCampaign._id) : undefined;

  // Inline rename (the title becomes an input). Editing an ONGOING campaign's
  // content isn't allowed — only its name — so this is the rename affordance.
  const [renaming, setRenaming] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const startRename = () => { setNameInput(drip?.name ?? ''); setRenaming(true); };
  const cancelRename = () => { setRenaming(false); };

  // FE#331: "Save as template" — save the whole sequence (steps + gaps) as a
  // reusable drip-template. The modal itself is rendered by the surface (both
  // the list card and the detail header render this hook), keyed on this flag.
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const startSaveTemplate = () => setSavingTemplate(true);
  const stopSaveTemplate = () => setSavingTemplate(false);
  const commitRename = () => {
    const n = nameInput.trim();
    if (n) {
      if (isStandalone && standaloneCampaign) {
        dispatch(DBCampaignThunks.patch({ entity: { ...standaloneCampaign, name: n } }));
      } else if (realDrip) {
        dispatch(DBDripThunks.patch({ entity: { ...realDrip, name: n } }));
      }
    }
    setRenaming(false);
  };

  // Delete = stop any in-flight sending, then soft-delete the sequence and its
  // step campaigns (both drop out of the list queries), then notify the caller.
  // Deleting a LIVE campaign is not the same act as deleting a draft: it stops
  // sending for everyone who has not received it yet, and messages already sent
  // cannot be recalled. "Click again to confirm" — the same gesture that deletes
  // a harmless template — is not a proportionate guard for that, so a live
  // campaign asks properly. A draft or a finished one keeps the light gesture.
  const isLiveDrip = drip?.status === 'active' || drip?.status === 'paused';

  const performDelete = async () => {
    if (!drip) return;
    // #590: the backend abort is the write that actually STOPS the send (it
    // flips the pending step campaign off `pending`). It used to be fired
    // un-awaited in a try/catch that swallowed any rejection, so a failed abort
    // was indistinguishable from a successful one and the UI proceeded to
    // optimistically show the drip as stopped while the scheduler still had a
    // live pending step. Await it and surface failure.
    let abortFailed = false;
    try {
      await abortDrip(dripId).unwrap();
    } catch {
      abortFailed = true;
    }
    dispatch(DBDripThunks.patch({ entity: { ...drip, status: 'aborted', deleted: true } }));
    // Step campaigns carry a dripId (never shown standalone), so aborting their
    // status stops any dispatch without needing a soft-delete flag.
    stepCampaigns.forEach((c) => dispatch(DBCampaignThunks.patch({ entity: { ...c, campaignStatus: 'aborted' } })));
    onDeleted?.(abortFailed ? { abortFailed: true } : undefined);
  };

  const handleDelete = () => {
    if (!drip) return;
    if (isLiveDrip) { setConfirmingDelete(true); return; }
    void performDelete();
  };

  const confirmDelete = () => { setConfirmingDelete(false); void performDelete(); };
  const cancelDelete = () => setConfirmingDelete(false);

  // Duplicate = a fresh draft copy (new step campaigns, no exit set), opened in
  // the wizard so the user can tweak + relaunch.
  const handleDuplicate = async () => {
    if (!drip) return;
    const copy: Drip = {
      name: `${drip.name ?? 'Untitled campaign'} (copy)`,
      boardIDArray: drip.boardIDArray,
      steps: drip.steps ?? [],
      stopOnReply: drip.stopOnReply,
      schedule: drip.schedule,
      currentStep: 0,
      status: 'draft',
    };
    const created = await dispatch(DBDripThunks.create(copy)).unwrap();
    if (typeof created?._id === 'string') onEdit?.(created._id);
  };

  // Standalone (legacy, drip-less) campaigns have no drip wizard, so we offer
  // only a soft-delete of the campaign itself — no Edit/Duplicate/Pause, and no
  // drip mutations (abortDrip/DBDripThunks would target a drip that doesn't
  // exist). Soft-delete drops it out of the list queries, then notify the caller.
  const handleDeleteStandalone = () => {
    if (!standaloneCampaign) return;
    // Abort as well as soft-delete: `deleted:true` only hides it from the list;
    // `campaignStatus:'aborted'` stops any queued/in-flight dispatch (matches the
    // drip path, which aborts its step campaigns rather than relying on `deleted`).
    dispatch(DBCampaignThunks.patch({ entity: { ...standaloneCampaign, campaignStatus: 'aborted', deleted: true } }));
    onDeleted?.();
  };

  // FE#240: stop a recurring campaign series — abort the current occurrence
  // (fails the ORCH#102 relaunch gate) and clear schedule.isRecurring so no
  // further occurrence fires. Keeps the (aborted) campaign visible; does not delete.
  const handleStopRecurringStandalone = () => {
    if (!standaloneCampaign) return;
    dispatch(DBCampaignThunks.patch({
      entity: {
        ...standaloneCampaign,
        campaignStatus: 'aborted',
        schedule: stopRecurrenceSchedulePatch(standaloneCampaign.schedule),
      },
    }));
  };

  const { t } = useTranslation(namespace);
  const isActiveOrPaused = drip?.status === 'active' || drip?.status === 'paused';
  const isDraft = drip?.status === 'draft';
  const renameItem: ActionItem = { key: 'rename', label: t('campaignsRedesign.actions.rename', 'Rename'), icon: Pencil, onSelect: startRename };
  const actionItems: ActionItem[] = [];
  if (isStandalone) {
    // FE#259: the standalone menu is built from the shared, tested key set
    // (standaloneCampaignActionKeys) so its contents — now including Duplicate,
    // which was previously missing → a completed campaign only showed
    // Rename/Delete — are consistent and unit-verified. The edit tiers keep the
    // #103 split: pending -> `schedule-editor` (message/audience/time editable,
    // "Edit campaign"), running/paused -> `campaign-editor` (message only, "Edit
    // message"). Each edit item is still gated on its own callback/id; a surface
    // that can't mount an editor simply omits that item.
    const standaloneTarget = standaloneEditTargetFor(standaloneCampaign?.campaignStatus);
    const keys = standaloneCampaignActionKeys(standaloneTarget, {
      isRecurring: isRecurringCampaign(standaloneCampaign),
    });
    for (const key of keys) {
      switch (key) {
        case 'rename':
          actionItems.push(renameItem);
          break;
        case 'edit-schedule':
          if (standaloneCampaignId && onEditSchedule) {
            actionItems.push({ key: 'edit-schedule', label: t('campaignsRedesign.actions.editCampaign', 'Edit campaign'), icon: CalendarClock, onSelect: () => onEditSchedule(standaloneCampaignId) });
          }
          break;
        case 'edit-content':
          if (standaloneCampaignId) {
            actionItems.push({ key: 'edit-content', label: t('campaignsRedesign.actions.editMessage', 'Edit message'), icon: Pencil, onSelect: () => onEditContent?.(standaloneCampaignId) });
          }
          break;
        case 'duplicate':
          // FE#259: standalone duplicate goes through duplicateCampaignThunk
          // (the persist-and-route path FE#252 fixed) — NOT the drip
          // handleDuplicate, which no-ops without a drip.
          if (standaloneCampaignId) {
            actionItems.push({ key: 'duplicate', label: t('campaignsRedesign.actions.duplicate', 'Duplicate'), icon: Copy, onSelect: () => { void dispatch(duplicateCampaignThunk(standaloneCampaignId)); } });
          }
          break;
        case 'stop-recurring':
          // "Stop recurring" named only half of what the handler does. It clears
          // `schedule.isRecurring` — the repeat — AND patches the campaign to
          // `aborted`, which cancels the run happening RIGHT NOW (that is the
          // point: aborting is what fails the ORCH#102 relaunch gate). Someone
          // reading "stop recurring" on a live campaign would reasonably expect
          // today's send to finish and only next week's to be cancelled. It is
          // the "Clear Messages" shape — a menu item whose label is a subset of
          // its effect — so the label now names both halves.
          actionItems.push({ key: 'stop-recurring', label: t('campaignsRedesign.actions.stopRecurring', 'Stop this and future sends'), icon: Repeat, danger: true, confirm: true, onSelect: handleStopRecurringStandalone });
          break;
        case 'delete':
          actionItems.push({ key: 'delete', label: t('campaignsRedesign.actions.delete', 'Delete campaign'), icon: Trash2, danger: true, confirm: true, onSelect: handleDeleteStandalone });
          break;
      }
    }
  } else if (drip) {
    if (isActiveOrPaused) {
      actionItems.push({
        key: 'pauseResume',
        label: drip.status === 'paused' ? 'Resume' : 'Pause',
        icon: drip.status === 'paused' ? PlayCircle : PauseCircle,
        // #590: Pause MUST call the backend `pause-drip` action, not just flip
        // the drip's status. A drip STEP is a real campaign; the scheduler's
        // due-query selects campaigns by `campaignStatus:'pending'` and never
        // reads the drip, so a paused drip only stops sending because
        // `pauseDripAction` flips the in-flight step campaign OFF `pending`.
        // (The prior code did a status-only optimistic patch on the belief that
        // "pause-drip only patches status" — it does not — so "paused" drips
        // kept firing on their due date. That was the #590 leak.)
        // We ALSO patch optimistically so the header + this menu reflect the new
        // status instantly, then await the backend action and revert on failure.
        onSelect: async () => {
          if (!realDrip) return;
          const resuming = drip.status === 'paused';
          // Optimistic status flip for instant UI (dbService).
          dispatch(DBDripThunks.patch({ entity: { ...realDrip, status: resuming ? 'active' : 'paused' } }));
          try {
            if (resuming) {
              // Resume catches up a step that completed while paused (the
              // completion hook's advance was skipped by the paused guard) and,
              // per #590, skips a missed past-due occurrence server-side.
              await resumeDrip(dripId).unwrap();
            } else {
              // The write that actually stops the send.
              await pauseDrip(dripId).unwrap();
            }
          } catch {
            // Revert the optimistic flip so the UI does not falsely claim the
            // drip is paused/resumed when the backend write failed.
            dispatch(DBDripThunks.patch({ entity: { ...realDrip, status: resuming ? 'paused' : 'active' } }));
          }
        },
      });
    }
    // #590: skip the NEXT occurrence — recurring drips only. Cancels the
    // imminent send and rolls the recurrence forward by one (server-side,
    // idempotent). Gated on `schedule.isRecurring`: a one-off drip has no "next"
    // occurrence to skip. `confirm:true` — skipping cancels a real upcoming send.
    if (drip.schedule?.isRecurring) {
      actionItems.push({
        key: 'skip-next',
        label: t('campaignsRedesign.actions.skipNext', 'Skip next send'),
        icon: SkipForward,
        confirm: true,
        onSelect: () => { void skipNextOccurrence(dripId).unwrap().catch(() => undefined); },
      });
    }
    // #103: a draft still reopens in the wizard. Beyond draft the wizard is OFF
    // LIMITS — its completion button re-launches the drip and mints a second
    // step-0 campaign over the same audience — so a launched-but-not-yet-
    // dispatching step goes to the tier-1 STEP editor (the same target the step
    // row picks, via `stepEditTargetFor`) and a dispatching one to the scoped
    // content editor. Rename remains available in every non-draft case.
    const menuTarget = menuEditTargetFor(drip.status, currentStepCampaign?.campaignStatus);
    if (menuTarget === 'wizard') {
      actionItems.push({ key: 'edit', label: t('common.edit', 'Edit'), icon: Pencil, onSelect: () => onEdit?.(dripId) });
    } else if (menuTarget === 'step-editor' && onEditStep) {
      // Gated on the callback: a surface that cannot mount `DripStepEditModal`
      // shows no tier-1 edit at all. It must NOT fall back to `onEdit` — that
      // is the launch wizard, and on a launched drip it double-sends.
      actionItems.push({
        key: 'edit',
        // Three different editors sat behind the single word "Edit": the launch
        // wizard (draft only), this per-step editor, and the scoped content
        // editor below. Naming what each one opens is the difference between a
        // menu and a guess — and this one opens the step that is up next, not
        // the whole campaign.
        label: t('campaignsRedesign.actions.editCurrentMessage', 'Edit current message'),
        icon: Pencil,
        onSelect: () => onEditStep(currentStepIndex),
      });
    } else if (menuTarget === 'campaign-editor' && stepCampaignId) {
      // No `?? dripId` fallback — see the standalone branch above.
      actionItems.push({
        key: 'edit-content',
        label: t('campaignsRedesign.actions.editMessage', 'Edit message'),
        icon: Pencil,
        onSelect: () => onEditContent?.(stepCampaignId),
      });
    }
    if (!isDraft) actionItems.push(renameItem);
    actionItems.push({ key: 'duplicate', label: t('campaignsRedesign.actions.duplicate', 'Duplicate'), icon: Copy, onSelect: () => { void handleDuplicate(); } });
    // FE#331: only offer "Save as template" when there is a sequence to save.
    if ((drip.steps?.length ?? 0) > 0) {
      actionItems.push({ key: 'save-template', label: t('campaignsRedesign.actions.saveAsTemplate', 'Save as template'), icon: Bookmark, onSelect: startSaveTemplate });
    }
    actionItems.push(
      { key: 'delete', label: t('campaignsRedesign.actions.delete', 'Delete campaign'), icon: Trash2, danger: true, confirm: !isLiveDrip, onSelect: handleDelete },
    );
  }

  return {
    items: actionItems,
    renaming,
    nameInput,
    setNameInput,
    startRename,
    commitRename,
    cancelRename,
    // FE#331 — the surface renders <SaveAsDripTemplateModal open={savingTemplate} … />
    savingTemplate,
    startSaveTemplate,
    /** A live campaign asks before it is deleted; the surface renders the dialog. */
    confirmingDelete,
    confirmDelete,
    cancelDelete,
    stopSaveTemplate,
  };
}
